/* ExposureJustification.js */

"use strict";
// Pure assembly of the per-CVE "justification bundle" downloaded from the
// Exposure Details drawer. Given the already-loaded evidence entities it
// produces the file map consumed by fileFactory.saveStringsAsZip:
// { "<name>": { type, content } }.
//
// Security stance (see caveats): raw device configuration is NEVER embedded —
// it can contain cleartext secrets (SNMP communities, PSKs, type-7 passwords)
// and no sanitized source exists.
const ExposureDispositions = require("../../ExposureDispositions");

const BUNDLE_SCHEMA_VERSION = "1.0";

const CAVEATS = [
    "The exposure predicate (rule) is machine-generated by an LLM and is unreviewed unless its reviewStatus indicates otherwise.",
    "Version decisions prefer the advisory's NIST CPE ranges (advisories[].nist) when present; for vendor-sourced advisories without CPE data (e.g. Cisco) the authoritative versions come from the vendor advisory (data.iosRelease / data.productNames) and the predicate's affectedVersions — a null nist block is expected for those, not a gap.",
    "Threat signals (EPSS / CISA KEV / public-exploit) are not yet integrated and are reported as null.",
    "No device configuration is included, raw or parsed: it can contain cleartext secrets and no sanitized source exists.",
    "deviceTags lists the capability tags from the device's CURRENT parse, not a snapshot from the run; deviceTags.matchesConfigAtRun says whether that parse came from the same configuration the verdict used.",
    "When present, the extraction response reflects the CURRENT rule version; if that differs from chainOfCustody.ruleVersionAtRun the verdict was produced by an earlier extraction (see rules[].isFromNewerRuleThanVerdict).",
    "The historical device signature is not snapshotted; verdict.matchedTags / verdict.missingTags are the surviving record of the tag reasoning, not a re-derivable input.",
];

function uniq(arr) {
    const out = [];
    (arr || []).forEach((v) => {
        if (v && out.indexOf(v) < 0) {
            out.push(v);
        }
    });
    return out;
}

function safeName(value) {
    return String(value || "unknown").replace(/[^a-zA-Z0-9._-]/g, "_");
}

function nullable(value) {
    return value === undefined ? null : value;
}

// Epoch millis / date -> ISO string for audit readability; passes through
// anything already human-readable, null for empty.
function toIso(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toISOString();
}

// Verdict stamps use a synthetic snapshot id "rawconfig:<md5>"; recover the
// hash so the RawConfig can be retrieved by { deviceId, sourceConfigHash }.
function extractConfigHashFromSnapshotId(snapshotId) {
    if (typeof snapshotId === "string" && snapshotId.indexOf("rawconfig:") === 0) {
        return snapshotId.slice("rawconfig:".length) || null;
    }
    return null;
}

function serializeProvenance(provenance) {
    if (!provenance) {
        return null;
    }
    const out = Object.assign({}, provenance);
    if (provenance.extractedAt !== undefined) {
        out.extractedAtIso = toIso(provenance.extractedAt);
    }
    return out;
}

/**
 * One line describing what the missing tags mean, for the plain-text bundle.
 *
 * Falls back to the flat count for verdicts predating the split — labelled, so a reader does not
 * mistake "we did not record this" for "we checked and found nothing".
 */
function tagEvidenceLine(v) {
    const matched = "Matched tags: " + (v.matchedTags || []).length;
    if (!v.tagEvidence) {
        return (
            matched +
            "  |  Feature tags missing: " +
            (v.missingTags || []).length +
            " (" +
            ExposureDispositions.TAG_EVIDENCE_LEGACY_NOTE +
            ")"
        );
    }
    // Labels come from the shared vocabulary, never retyped here. This line used to say "Cannot
    // verify (no detection rule)" while the UI said "Cannot verify … no detection rule yet — check
    // these on the device" for the identical bucket, so the bundle an auditor reads and the screen
    // an operator reads described the same evidence in two different terms.
    const parts = [matched];
    const notConfigured = v.tagEvidence.notConfigured || [];
    const notObservable = v.tagEvidence.notObservable || [];
    const notEvaluated = v.tagEvidence.notEvaluated || [];
    if (notConfigured.length) {
        parts.push(
            ExposureDispositions.TAG_EVIDENCE_LABELS.notConfigured + ": " + notConfigured.join(", ")
        );
    }
    if (notObservable.length) {
        parts.push(
            ExposureDispositions.TAG_EVIDENCE_LABELS.notObservable +
                " (" +
                ExposureDispositions.TAG_EVIDENCE_HINTS.notObservable +
                "): " +
                notObservable.join(", ")
        );
    }
    if (notEvaluated.length) {
        parts.push(
            ExposureDispositions.TAG_EVIDENCE_AUDIT_LABELS.notEvaluated +
                ": " +
                notEvaluated.join(", ")
        );
    }
    return parts.join("  |  ");
}

function serializeVerdict(entry) {
    return {
        score: entry.score || null,
        // Qualified: a version-only red is stated as such here too, so the bundle cannot read
        // stronger than the screen it was downloaded from.
        disposition: ExposureDispositions.dispositionLabel(entry.score, entry.reasonCode),
        reasonCode: entry.reasonCode || null,
        reasonText: entry.reasonText || null,
        // Both forms: the enum is the machine-comparable record an auditor diffs between runs,
        // the label is what makes the JSON readable without the scorer source open alongside.
        decidedAt: entry.decidedAt || null,
        decidedAtLabel: entry.decidedAt
            ? ExposureDispositions.decidedAtLabel(entry.decidedAt)
            : null,
        matchedTags: entry.matchedTags || [],
        missingTags: entry.missingTags || [],
        // Split by meaning (S4). Null on verdicts scored before the split, which the audit reader
        // must be able to tell apart from "the split ran and found nothing".
        tagEvidence: entry.tagEvidence || null,
        versionCheck: entry.versionCheck || null,
        platformCheck: entry.platformCheck || null,
        // Same null-vs-[] convention as tagEvidence directly above, and for the same reason: an
        // audit reader must be able to tell "scored before config determination existed" from
        // "matchers ran and matched nothing". Those are different claims about the same device.
        //
        // Matched lines arrive ALREADY REDACTED — the backend redacts before persisting, so the
        // bundle cannot embed a secret that the stored verdict does not already contain. Redacting
        // here instead would leave the secret in the database and in every other export.
        configEvidence: entry.configEvidence === undefined ? null : entry.configEvidence,
        configSnapshotIdAtRun: entry.configSnapshotIdAtRun || null,
        runtimeEvidence: entry.runtimeEvidence || [],
        contributingAdvisoryIds: entry.contributingAdvisoryIds || [],
        recommendedRemediationAtRun: entry.recommendedRemediationAtRun || null,
        threatSignals: nullable(entry.threatSignals),
        advisorySource: entry.advisorySource || null,
    };
}

function serializeStamps(entry) {
    return {
        runId: entry.runId || null,
        ruleVersionAtRun: nullable(entry.ruleVersionAtRun),
        signatureHashAtRun: entry.signatureHashAtRun || null,
        snapshotIdAtRun: entry.snapshotIdAtRun || null,
        deviceVersionAtRun: entry.deviceVersionAtRun || null,
        versionReadingAtRun: entry.versionReadingAtRun || null,
        deviceOsAtRun: entry.deviceOsAtRun || null,
        p35VersionAtRun: nullable(entry.p35VersionAtRun),
        qualifiedAt: nullable(entry.qualifiedAt),
        qualifiedAtIso: toIso(entry.qualifiedAt),
    };
}

// Advisory data (title/summary/SIR/CVSS) and the authoritative NIST CPE
// configurations are public advisory content — included verbatim.
function serializeAdvisory(advisory, advisoryId) {
    if (!advisory) {
        return {advisoryId: advisoryId, available: false};
    }
    return {
        advisoryId: advisoryId,
        displayName: advisory.displayName || null,
        data: advisory.data || null,
        nist: advisory.nist || null,
    };
}

function serializeRule(rule, advisoryId, entry) {
    if (!rule) {
        return {advisoryId: advisoryId, available: false};
    }
    // rawResponse (up to 256KB) is moved to its own file; keep the rest of the
    // extraction telemetry inline.
    let extraction = null;
    if (rule.extraction) {
        extraction = Object.assign({}, rule.extraction);
        delete extraction.rawResponse;
    }
    return {
        advisoryId: advisoryId,
        version: nullable(rule.version),
        status: rule.status || null,
        reviewStatus: rule.reviewStatus || null,
        staleForReview: rule.staleForReview === true,
        isFromNewerRuleThanVerdict:
            advisoryId === entry.advisoryId &&
            rule.version !== null &&
            rule.version !== undefined &&
            entry.ruleVersionAtRun !== null &&
            entry.ruleVersionAtRun !== undefined &&
            rule.version !== entry.ruleVersionAtRun,
        predicate: rule.predicate || null,
        provenance: serializeProvenance(rule.provenance),
        extraction: extraction,
        lastFailedExtraction: rule.lastFailedExtraction || null,
        extractionError: (rule.extractionError && rule.extractionError.message) || null,
        extractedAt: nullable(rule.extractedAt),
        extractedAtIso: toIso(rule.extractedAt),
        extractedByModel: rule.extractedByModel || null,
        llmConnectionId: rule.llmConnectionId || null,
        advisorySource: rule.advisorySource || null,
    };
}

function describeDeviceTagsUnavailable(error) {
    if (!error) {
        return "Not included in this download: the device has no parsed configuration yet.";
    }
    if (error.errorCode === "NoDevice") {
        return "Not included in this download: no device was selected when it was generated.";
    }
    if (error.errorCode === "NotAuthorized") {
        return "Not included in this download: the device's tags are not readable by the account that generated this download.";
    }
    return "Not included in this download: the device's tags could not be loaded.";
}

function serializeDeviceTags(source, error, configHashAtRun) {
    if (!source) {
        return {available: false, reason: describeDeviceTagsUnavailable(error)};
    }
    const sourceConfigHash =
        typeof source.sourceConfigHash === "string" ? source.sourceConfigHash : null;
    return {
        available: true,
        tags: uniq((source.tags || []).filter((tag) => typeof tag === "string")).sort(),
        tagRulesVersion: nullable(source.tagRulesVersion),
        generatedAt: toIso(source.generatedAt),
        sourceConfigHash: sourceConfigHash,
        matchesConfigAtRun:
            sourceConfigHash && configHashAtRun ? sourceConfigHash === configHashAtRun : null,
    };
}

function serializePrefs(prefs) {
    return {
        captureExtractionTranscript: nullable(prefs.captureExtractionTranscript),
        runtimeDeepeningMode: nullable(
            prefs.runtimeDeepeningMode !== undefined
                ? prefs.runtimeDeepeningMode
                : prefs.deepeningMode
        ),
    };
}

function buildReadme(bundle) {
    const v = bundle.verdict;
    const c = bundle.chainOfCustody;
    const dev = bundle.device || {};
    const winningRule = (bundle.rules || []).find(
        (r) => r.advisoryId === bundle.advisories.winning.advisoryId
    );
    const lines = [
        "Exposure Justification Bundle",
        "=============================",
        "Generated: " + (bundle.generatedAt || "—"),
        "Device: " +
            (dev.name || "—") +
            " (" +
            (dev.id || "—") +
            ") — OS " +
            (dev.os || "—") +
            ", version " +
            (dev.version || "—"),
        "CVE: " + (bundle.cve.id || "—") + " — Disposition: " + bundle.cve.disposition,
        "",
        "Why this verdict",
        "----------------",
        "Reason: " + (v.reasonText || "—") + (v.reasonCode ? " [" + v.reasonCode + "]" : ""),
        // The label, not the enum: this is the human-readable half of the bundle. The enum stays
        // available in verdict.json for anyone diffing runs.
        "Decided on: " +
            (v.decidedAt
                ? ExposureDispositions.decidedAtLabel(v.decidedAt)
                : "— (scored before the deciding level was recorded)"),
        // This is an audit artifact read on its own, away from the grid, so it names the tags
        // rather than only counting them — and says what each absence means. A bare count of
        // "missing" tags is what made an unevaluated verdict look evidenced.
        tagEvidenceLine(v),
        winningRule && winningRule.available !== false
            ? "Rule: v" +
              (winningRule.version === null || winningRule.version === undefined
                  ? "—"
                  : winningRule.version) +
              " (" +
              (winningRule.reviewStatus || "unreviewed") +
              ")" +
              (winningRule.extractedByModel ? " extracted by " + winningRule.extractedByModel : "")
            : "Rule: (not available)",
        "Run: " + (c.runId || "—") + " at " + (c.qualifiedAt || "—"),
        "",
        "Contents",
        "--------",
        "  justification.json          full structured evidence (advisory + CPE ranges, rule predicate, verdict, audit stamps, org prefs, device tags)",
        "  extraction-response-*.txt   raw model output per rule, when captured (no prompt)",
        "  README.txt                  this file",
        "",
        "Caveats",
        "-------",
    ].concat(bundle.caveats.map((line) => "  - " + line));
    return lines.join("\n") + "\n";
}

/**
 * Build the zip file map for the justification bundle.
 * @param {Object} data - already-loaded evidence:
 *   { cveId, generatedAt, exporter, org, device, entry,
 *     advisoriesById:Map, rulesByAdvisoryId:Map, deviceTags, deviceTagsError, prefs }
 * @returns {Object} fileMap for fileFactory.saveStringsAsZip
 */
function buildJustificationFileMap(data) {
    const entry = data.entry || {};
    const configHashAtRun = extractConfigHashFromSnapshotId(entry.snapshotIdAtRun) || null;
    const advisoriesById = data.advisoriesById || new Map();
    const rulesByAdvisoryId = data.rulesByAdvisoryId || new Map();
    const advIds = uniq([entry.advisoryId].concat(entry.contributingAdvisoryIds || []));

    const files = {};
    const getAdv = (id) =>
        (advisoriesById.get ? advisoriesById.get(id) : advisoriesById[id]) || null;
    const getRule = (id) =>
        (rulesByAdvisoryId.get ? rulesByAdvisoryId.get(id) : rulesByAdvisoryId[id]) || null;

    const rules = advIds.map((advId) => {
        const rule = getRule(advId);
        const serialized = serializeRule(rule, advId, entry);
        const raw = rule && rule.extraction && rule.extraction.rawResponse;
        if (raw) {
            const fname = "extraction-response-" + safeName(advId) + ".txt";
            files[fname] = {type: "text/plain", content: String(raw)};
            serialized.extraction = serialized.extraction || {};
            serialized.extraction.rawResponseFile = fname;
        }
        return serialized;
    });

    const bundle = {
        bundleSchemaVersion: BUNDLE_SCHEMA_VERSION,
        generatedAt: data.generatedAt || null,
        exportedBy: data.exporter || null,
        organization: data.org || null,
        device: data.device || null,
        cve: {
            id: entry.cveId || data.cveId || null,
            score: entry.score || null,
            // Qualified, matching verdict.disposition — the summary header and the verdict block
            // must not disagree about how strong the finding is.
            disposition: ExposureDispositions.dispositionLabel(entry.score, entry.reasonCode),
        },
        verdict: serializeVerdict(entry),
        chainOfCustody: serializeStamps(entry),
        advisories: {
            winning: serializeAdvisory(getAdv(entry.advisoryId), entry.advisoryId || null),
            contributing: advIds
                .filter((id) => id !== entry.advisoryId)
                .map((id) => serializeAdvisory(getAdv(id), id)),
        },
        rules: rules,
        deviceTags: serializeDeviceTags(data.deviceTags, data.deviceTagsError, configHashAtRun),
        discoveryInput: {
            note: "No device configuration is embedded, raw or parsed: it can contain cleartext secrets and no sanitized source exists.",
            snapshotIdAtRun: entry.snapshotIdAtRun || null,
            signatureHashAtRun: entry.signatureHashAtRun || null,
            sourceConfigHash: configHashAtRun,
            retrieval:
                "Gluware can retrieve the RawConfig server-side by { deviceId, sourceConfigHash }.",
        },
        orgPrefs: data.prefs
            ? serializePrefs(data.prefs)
            : {available: false, reason: "ExposureManagementPrefs not available."},
        caveats: CAVEATS,
    };

    files["justification.json"] = {
        type: "application/json",
        content: JSON.stringify(bundle, null, "\t"),
    };
    files["README.txt"] = {type: "text/plain", content: buildReadme(bundle)};
    return files;
}

module.exports = {
    buildJustificationFileMap: buildJustificationFileMap,
    BUNDLE_SCHEMA_VERSION: BUNDLE_SCHEMA_VERSION,
    CAVEATS: CAVEATS,
};
